/**
 * Vendored from angular2-text-mask (View Engine metadata incompatible with strict Ivy AOT).
 * Uses text-mask-core; selector and API match the original [textMask] directive.
 */
import {
  Directive,
  ElementRef,
  forwardRef,
  Inject,
  Input,
  Optional,
  Provider,
  Renderer2,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import {
  COMPOSITION_BUFFER_MODE,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { createTextMaskInputElement } from 'text-mask-core/dist/textMaskCore';

export class TextMaskConfig {
  mask: Array<string | RegExp> | ((raw: string) => Array<string | RegExp>) | false;
  guide?: boolean;
  placeholderChar?: string;
  pipe?: (conformedValue: string, config: TextMaskConfig) => false | string | object;
  keepCharPositions?: boolean;
  showMask?: boolean;
}

export const MASKEDINPUT_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MaskedInputDirective),
  multi: true,
};

function isAndroidUserAgent(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /android (\d+)/.test(navigator.userAgent.toLowerCase());
}

@Directive({
  selector: '[textMask]',
  exportAs: 'textMask',
  providers: [MASKEDINPUT_VALUE_ACCESSOR],
  host: {
    '(input)': '_handleInput($event.target.value)',
    '(blur)': 'onTouched()',
    '(compositionstart)': '_compositionStart()',
    '(compositionend)': '_compositionEnd($event.target.value)',
  },
})
export class MaskedInputDirective implements ControlValueAccessor, OnChanges {
  @Input('textMask') textMaskConfig: TextMaskConfig = {
    mask: [],
    guide: true,
    placeholderChar: '_',
    pipe: undefined,
    keepCharPositions: false,
  };

  onChange = (_: unknown) => {};
  onTouched = () => {};

  private textMaskInputElement: { update: (v: unknown) => void } | undefined;
  private inputElement?: HTMLInputElement;

  private composing = false;

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(COMPOSITION_BUFFER_MODE) private compositionMode: boolean | null
  ) {
    if (this.compositionMode == null) {
      this.compositionMode = !isAndroidUserAgent();
    }
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.setupMask(true);
    if (this.textMaskInputElement !== undefined && this.inputElement) {
      this.textMaskInputElement.update(this.inputElement.value);
    }
  }

  writeValue(value: unknown): void {
    this.setupMask();

    const normalizedValue = value == null ? '' : String(value);
    if (this.inputElement) {
      this.renderer.setProperty(this.inputElement, 'value', normalizedValue);
    }

    if (this.textMaskInputElement !== undefined) {
      this.textMaskInputElement.update(value as string);
    }
  }

  registerOnChange(fn: (_: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  _handleInput(value: string): void {
    if (!this.compositionMode || (this.compositionMode && !this.composing)) {
      this.setupMask();

      if (this.textMaskInputElement !== undefined && this.inputElement) {
        this.textMaskInputElement.update(value);
        const next = this.inputElement.value;
        this.onChange(next);
      }
    }
  }

  setupMask(create = false): void {
    if (!this.inputElement) {
      const el = this.elementRef.nativeElement;
      if (el.tagName.toUpperCase() === 'INPUT') {
        this.inputElement = el as HTMLInputElement;
      } else {
        this.inputElement = el.getElementsByTagName('INPUT')[0] as HTMLInputElement;
      }
    }

    if (this.inputElement && create) {
      this.textMaskInputElement = createTextMaskInputElement(
        Object.assign({ inputElement: this.inputElement }, this.textMaskConfig)
      );
    }
  }

  _compositionStart(): void {
    this.composing = true;
  }

  _compositionEnd(value: string): void {
    this.composing = false;
    this.compositionMode && this._handleInput(value);
  }
}
